/* eslint-disable @typescript-eslint/ban-ts-comment */
import { AxiosRequestConfig, AxiosResponse, AxiosError, AxiosInstance, CancelToken } from 'axios';

import Service, { EnvType as EnvTypeAlias } from './service';
import EventBus from '../event';
import { transformErrorForSerialization } from '../error';
import { getRequestKey } from './tools';

export type EnvType = EnvTypeAlias;
export interface SMHErrorData {
  code: string;
  inner?: string;
  message: string;
  requestId: string;
  extraInfo?: {
    [key: string]: any;
  };
}

export interface SMHAsyncErrorData {
  result: {
    code: string;
    message: string;
    requestId: string;
    smhStatus: number;
  };
}

export interface GetHttpConfig {
  baseURL?: string;
  envType?: EnvType;
  retry?: number;
  eventBus?: EventBus;
  cancelToken?: CancelToken | null;
}

export const DEFAULT_BASE_URL = 'https://pan.tencentsmh.cn';
export const DEFAULT_ENV_TYPE = ((): EnvType => {
  try {
    // 获取默认的env_type
    if (window.constructor) {
      const { name } = window.constructor;
      return name === 'Object' ? 'node' : 'web';
    }
  } catch (error) {
    return 'web';
  }
  return 'web';
})();
export const DEFAULT_RETRY = 3;

const getHttp = (
  { baseURL = '', envType = DEFAULT_ENV_TYPE, retry = DEFAULT_RETRY, eventBus, cancelToken = null }: GetHttpConfig = {
    baseURL: '',
    envType: DEFAULT_ENV_TYPE,
    retry: DEFAULT_RETRY,
    cancelToken: null,
  },
): AxiosInstance => {
  const service = new Service(baseURL, envType, retry, cancelToken);
  /**
   * TODO: 取消重复请求
   *
  const pendingRequest = new Map<string, Canceler>();
  // 是否存在重复请求
  function existRepeatRequest(config: AxiosRequestConfig) {
    const requestKey = getRequestKey(config);
    return pendingRequest.has(requestKey);
  }
  // 添加请求
  function addPendingRequest(config: AxiosRequestConfig) {
    const requestKey = getRequestKey(config);
    config.cancelToken =
      config.cancelToken ||
      new axios.CancelToken(cancel => {
        if (!pendingRequest.has(requestKey)) {
          pendingRequest.set(requestKey, cancel);
        }
      });
  }
  // 取消重复请求，移除重复请求信息
  function removePendingRequest(config: AxiosRequestConfig) {
    const requestKey = getRequestKey(config);
    if (existRepeatRequest(config)) {
      // 如果是重复的请求，则执行对应的cancel函数
      const cancel = pendingRequest.get(requestKey);
      cancel?.('canceled');
      // 将前一次重复的请求移除
      pendingRequest.delete(requestKey);
    }
  }
  ***/

  // 请求拦截器
  service.interceptors.request.use(
    async (config: AxiosRequestConfig) => {
      /**
       * TODO: 取消重复请求
       *
      // 检查是否存在重复请求，若存在则取消已发的请求
      if (existRepeatRequest(config)) {
        removePendingRequest(config);
      } else {
      eventBus && eventBus.emit('request', { config });
      }
      // 把当前请求信息添加到pendingRequest对象中
      addPendingRequest(config);
      ***/

      eventBus && eventBus.emit('request', { config });
      return config;
    },
    (error: AxiosError) => {
      eventBus && eventBus.emit('request', { config: error.config, error });
      return Promise.resolve(error);
    },
  );

  // 响应拦截器
  service.interceptors.response.use(
    (response: AxiosResponse): Promise<any> => {
      // // 有响应后移除缓存的请求
      // removePendingRequest(response.config);
      // 正常返回
      const { headers } = response;
      const res = {
        ...response,
        requestId: headers['x-request-id'],
      };
      const { data, status, requestId, statusText } = res;
      const formatData = { headers, data, requestId, status, statusText };
      eventBus && eventBus.emit('response', { data: formatData, config: res.config });
      return Promise.resolve(res);
    },
    async (error: any): Promise<CustomErrorData | any> => {
      // // 有响应后移除缓存的请求
      // removePendingRequest(error.config || {});

      // 错误在调用的catch里处理
      const customErrorData: CustomErrorData = {
        code: 'UnknownError',
        inner: '',
        message: 'SMH Http caught an unknown error',
        status: 0,
        headers: {},
        requestId: '',
        extraInfo: {},
      };
      if ('isAxiosError' in error && error.isAxiosError) {
        // 认为 error 是一个 AxiosError
        const { response } = error as AxiosError<string | SMHErrorData | SMHAsyncErrorData>;
        if (response) {
          const { data, status, headers } = response;
          let { code, inner, message, requestId, extraInfo } = customErrorData;
          // 兼容404响应
          if (status === 404 && typeof data !== 'object') {
            code = data === 'Not Found' ? 'APINotFound' : response.statusText.split(' ').join('');
            inner = code;
            message = data === 'Not Found' ? 'API Not Found' : response.statusText;
            requestId = headers['x-request-id'] || '';
          } else if (data !== null && typeof data === 'object') {
            if ('code' in (data as SMHErrorData | SMHAsyncErrorData)) {
              ({ code, inner, message, requestId, extraInfo } = data as SMHErrorData);
            } else if ('result' in (data as SMHAsyncErrorData)) {
              let res = (data as SMHAsyncErrorData).result;
              if (Array.isArray(res)) {
                [res] = res;
              }
              ({ code, message, requestId } = res);
            }
          } else if ('code' in error) {
            ({ code, message } = error.toJSON());
          }
          Object.assign(customErrorData, { code, inner, message, status, headers, requestId, extraInfo });
          // userToken过期，直接退出登录
          // 用户冻结或被删除，直接退出登录
          // 用户账号被注销，直接退出登录
          // 用户ip被限制，直接退出登录
          const signOutCode = [
            'InvalidUserToken',
            'EmptyUserToken',
            'UserDisabled',
            'UserNotAllowed',
            'OrganizationUserUnderDeregister',
            'IpIsNotAllowed',
          ].includes(code);
          if (signOutCode) {
            eventBus && eventBus.emit('sign-out');
            return Promise.reject(customErrorData);
          }
          if (['OrganizationServiceTimeExpired', 'LibraryServiceTimeExpired'].includes(code)) {
            eventBus && eventBus.emit('org-expired');
            return Promise.reject(customErrorData);
          }
        } else {
          const { code, message } = error.toJSON();
          Object.assign(customErrorData, { code: code || message, message });
        }
      } else if ('code' in (error || {})) {
        // 认为 error 是一个 Node（系统、运行环境）相关的错误
        const { code, message } = transformErrorForSerialization(error);
        Object.assign(customErrorData, { code, message });
      } else {
        const tmpError = transformErrorForSerialization(error);
        customErrorData.message = tmpError.message;
      }
      error.config && eventBus && eventBus.emit('response', { error: customErrorData, config: error.config || {} });
      return Promise.reject(customErrorData);
    },
  );

  return service as unknown as AxiosInstance;
};

export default getHttp;
